import { CheckIcon } from 'lucide-react';
import { cn } from '@noor/lib/cn';

export interface WizardStep {
  /** Stable key, used for React's list identity. */
  id: string;
  /** Caption under the circle («Детали» / «Добавление продукта»). */
  label: string;
}

interface WizardStepsProps {
  steps: WizardStep[];
  /** Zero-based index of the step being shown. */
  current: number;
  /** Jump to an already-completed step; omitted makes the circles inert. */
  onSelect?: (index: number) => void;
}

/**
 * The numbered step indicator at the top of the branch wizard (Figma
 * 1066:11135 step 1, 1067:12534 step 2).
 *
 * Each step is a 32px circle over a 14px caption, joined by an 84px hairline.
 * Three visual states, all from the design: the CURRENT step is an orange ring
 * around its orange number, a COMPLETED one is a solid orange disc with a white
 * check, and a step not yet reached is a grey ring around grey text.
 *
 * The circles are `<button>`s only when `onSelect` is given, and only a
 * completed step is clickable — stepping FORWARD has to run step 1's validation
 * first, which is the parent's job, so this component never offers it.
 */
export function WizardSteps({ steps, current, onSelect }: WizardStepsProps) {
  return (
    <ol className="flex items-start justify-center">
      {steps.map((step, index) => {
        const isComplete = index < current;
        const isCurrent = index === current;
        // Only a step already finished is safe to jump to; see the note above.
        const canSelect = onSelect !== undefined && isComplete;

        return (
          <li key={step.id} className="flex items-start">
            {index > 0 && (
              /* The 84px connector, sitting on the circles' vertical centre. */
              <span aria-hidden="true" className="mx-2 mt-4 h-px w-[84px] bg-border" />
            )}
            {/* Wide enough for «Добавление продукта» to stay on ONE line, as
             * the design sets it — the caption is centred under its circle
             * and overflows the column symmetrically rather than wrapping. */}
            <div className="flex w-8 flex-col items-center gap-2.5">
              {/* Radix has no stepper primitive and the design's states are
               * three fills of one 32px circle, so this is a plain element
               * rather than a borrowed control. */}
              <button
                type="button"
                disabled={!canSelect}
                aria-current={isCurrent ? 'step' : undefined}
                onClick={canSelect ? () => onSelect(index) : undefined}
                className={cn(
                  'flex size-8 shrink-0 items-center justify-center rounded-full',
                  'text-sm font-medium tracking-tight transition-colors outline-none',
                  'focus-visible:ring-[3px] focus-visible:ring-ring/40',
                  isComplete && 'bg-primary text-primary-foreground',
                  isCurrent && 'border-2 border-primary text-primary',
                  !isComplete &&
                    !isCurrent &&
                    'border border-border text-muted-foreground',
                  canSelect ? 'cursor-pointer' : 'cursor-default',
                )}
              >
                {isComplete ? (
                  <CheckIcon className="size-4" strokeWidth={3} />
                ) : (
                  // Two digits, as the design draws them («01», «02»).
                  String(index + 1).padStart(2, '0')
                )}
              </button>
              <span
                className={cn(
                  'text-center text-sm font-medium tracking-tight whitespace-nowrap',
                  isCurrent || isComplete ? 'text-foreground' : 'text-muted-foreground',
                )}
              >
                {step.label}
              </span>
            </div>
          </li>
        );
      })}
    </ol>
  );
}
